import { useCallback, useEffect, useMemo, useState } from 'react'
import {
	AppButton,
	Input,
	Table,
	TableHeader,
	TableBody,
	TableRow,
	TableHead,
	TableCell,
	DotMatrix,
	Pagination,
	Sheet,
	CollapsibleCard,
	Modal,
	ModalHeader
} from '@clickguard/design-system'
import SearchSm from '@untitled-ui/icons-react/build/esm/SearchSm'
import { useAccountStore, useAssetStore, useUserStore } from 'store'
import { Link } from 'react-router-dom'
import { GEmpty } from 'components/basic-blocks/g-empty'
import { createCrmApi, CrmConnection, CrmFormReference } from 'services/crm/api'
import { useHubSpotConnect } from 'services/crm/use-hubspot-connect'
import { errorMessage } from 'pages/workspace/asset/lead-forms/api'
import { NewCrmModal } from './new-crm-modal'
import { SortHeadCell, SortState, nextSortState } from './sort-head-cell'

export const CrmTab = () => {
	const account = useAccountStore((s) => s.account)
	return <CrmTabContent key={account?.id || ''} />
}

const CrmTabContent = () => {
	const account = useAccountStore((s) => s.account)
	const asset = useAssetStore((s) => s.asset)
	const user = useUserStore((s) => s.user)
	const userAccess = useAccountStore((s) => s.userAccess)
	const canWrite = ['OWNER', 'WRITE'].includes(userAccess(user?.id || '') || '')
	const api = useMemo(() => createCrmApi(account?.id || ''), [account?.id])
	const [rows, setRows] = useState<CrmConnection[]>([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')
	const [modal, setModal] = useState(false)
	const [search, setSearch] = useState('')
	const [sort, setSort] = useState<SortState>({ key: 'name', dir: 'asc' })
	const [page, setPage] = useState(1)
	const [selected, setSelected] = useState<CrmConnection | null>(null)
	const [forms, setForms] = useState<CrmFormReference[]>([])
	const [detailLoading, setDetailLoading] = useState(false)
	const [confirmation, setConfirmation] = useState(false)
	const [deleting, setDeleting] = useState(false)
	const reload = useCallback(async () => {
		setLoading(true)
		setError('')
		try {
			const connections = (await api.list()).connections
			setRows(connections)
			setSelected((current) =>
				current ? connections.find((c) => c.id === current.id) || null : null
			)
		} catch (e) {
			setError(errorMessage(e))
		} finally {
			setLoading(false)
		}
	}, [api])
	useEffect(() => {
		let active = true
		setRows([])
		setSelected(null)
		setModal(false)
		setLoading(true)
		setError('')
		void api
			.list()
			.then((r) => {
				if (active) setRows(r.connections)
			})
			.catch((e) => {
				if (active) setError(errorMessage(e))
			})
			.finally(() => {
				if (active) setLoading(false)
			})
		return () => {
			active = false
		}
	}, [api])
	useEffect(() => {
		if (!selected) return
		let active = true
		setDetailLoading(true)
		setForms([])
		void api
			.detail(selected.id)
			.then((r) => {
				if (active) {
					setForms(r.forms)
					setSelected(r)
				}
			})
			.catch((e) => {
				if (active) setError(errorMessage(e))
			})
			.finally(() => {
				if (active) setDetailLoading(false)
			})
		return () => {
			active = false
		}
	}, [api, selected?.id])
	const oauth = useHubSpotConnect(api, (c) => {
		setSelected(c)
		void reload()
	})
	const filtered = rows
		.filter((r) =>
			`${r.name} ${r.portal_id}`.toLowerCase().includes(search.toLowerCase())
		)
		.sort((a, b) =>
			sort
				? (sort.key === 'status'
						? a.status.localeCompare(b.status)
						: a.name.localeCompare(b.name)) * (sort.dir === 'asc' ? 1 : -1)
				: 0
		)
	const pages = Math.max(1, Math.ceil(filtered.length / 10))
	const currentPage = Math.min(page, pages)
	const startDelete = async () => {
		if (!selected) return
		setDetailLoading(true)
		try {
			setForms((await api.impact(selected.id)).forms)
			setConfirmation(true)
		} catch (e) {
			setError(errorMessage(e))
		} finally {
			setDetailLoading(false)
		}
	}
	return (
		<div className="flex flex-col gap-4 pt-4">
			<div className="flex items-center justify-between gap-3">
				<Input
					state="default"
					size="sm"
					leadingIcon={<SearchSm />}
					placeholder="Search"
					aria-label="Search CRM connections"
					value={search}
					onChange={(e) => {
						setSearch(e.target.value)
						setPage(1)
					}}
				/>
				<div className="flex gap-3">
					<AppButton
						variant="secondary"
						size="sm"
						label="Refresh"
						disabled={loading}
						onClick={() => void reload()}
					/>
					<AppButton
						variant="primary"
						size="sm"
						label="Connect new CRM"
						disabled={!canWrite}
						onClick={() => setModal(true)}
					/>
				</div>
			</div>
			{(error || oauth.error) && (
				<p role="alert" className="text-sm text-red-600">
					{error || oauth.error}
				</p>
			)}
			<div className="overflow-x-auto">
				<Table>
					<TableHeader className="cg-bg-surface-2">
						<TableRow className="hover:!bg-transparent">
							<SortHeadCell
								label="CRM account"
								sortKey="name"
								sort={sort}
								onSort={(key) => setSort(nextSortState(sort, key))}
							/>
							<TableHead>Account ID</TableHead>
							<TableHead>Credential</TableHead>
							<SortHeadCell
								label="Status"
								sortKey="status"
								sort={sort}
								onSort={(key) => setSort(nextSortState(sort, key))}
							/>
							<TableHead>
								<span className="sr-only">Actions</span>
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{loading ? (
							<TableRow>
								<TableCell colSpan={5}>
									<div className="flex justify-center p-8">
										<DotMatrix label="Loading CRM connections" />
									</div>
								</TableCell>
							</TableRow>
						) : !filtered.length ? (
							<TableRow>
								<TableCell colSpan={5}>
									<GEmpty
										title={
											search
												? 'No matching connections'
												: 'No CRM connections yet'
										}
										description="Connect HubSpot to flag lead deliverability in your CRM."
									/>
								</TableCell>
							</TableRow>
						) : (
							filtered
								.slice((currentPage - 1) * 10, currentPage * 10)
								.map((r) => (
									<TableRow key={r.id}>
										<TableCell>{r.name}</TableCell>
										<TableCell>{r.portal_id}</TableCell>
										<TableCell>{r.credential_identity || 'HubSpot'}</TableCell>
										<TableCell>
											{r.status === 'ACTIVE' ? 'Connected' : 'No access'}
										</TableCell>
										<TableCell>
											<AppButton
												variant="secondary"
												size="sm"
												label="Settings"
												onClick={() => setSelected(r)}
											/>
										</TableCell>
									</TableRow>
								))
						)}
					</TableBody>
				</Table>
			</div>
			{!!filtered.length && (
				<Pagination
					page={currentPage}
					totalPages={pages}
					onPageChange={setPage}
				/>
			)}
			<NewCrmModal
				open={modal}
				onClose={() => setModal(false)}
				api={api}
				assetId={asset?.account_id === account?.id ? asset?.id : undefined}
				onConnected={() => void reload()}
			/>
			<Sheet
				open={!!selected}
				onClose={() => setSelected(null)}
				title="CRM settings"
				side="right"
				width={640}
			>
				<div className="flex flex-col gap-4 p-1">
					{selected && (
						<>
							<CollapsibleCard title="Overview" defaultExpanded>
								<dl className="grid grid-cols-2 gap-3 text-sm">
									<dt>Name</dt>
									<dd>{selected.name}</dd>
									<dt>ID</dt>
									<dd>{selected.portal_id}</dd>
									<dt>Connected at</dt>
									<dd>{new Date(selected.created_at).toLocaleString()}</dd>
									<dt>Connected by</dt>
									<dd>{selected.connected_by_name || selected.connected_by}</dd>
									<dt>Credential</dt>
									<dd>{selected.credential_identity || 'HubSpot'}</dd>
									<dt>Status</dt>
									<dd>
										{selected.status === 'ACTIVE' ? 'Connected' : 'No access'}
									</dd>
								</dl>
								<div className="mt-4 flex gap-3">
									{oauth.busy ? (
										<DotMatrix label="Connecting HubSpot" />
									) : (
										<>
											<AppButton
												label="New credential"
												variant="secondary"
												disabled={!canWrite}
												onClick={() => void oauth.connect(selected.id)}
											/>
											{selected.status === 'NEEDS_REAUTH' && (
												<AppButton
													label="Reauthorize"
													disabled={!canWrite}
													onClick={() => void oauth.connect(selected.id)}
												/>
											)}
										</>
									)}
								</div>
							</CollapsibleCard>
							<CollapsibleCard
								title="Lead forms using this connection"
								defaultExpanded
							>
								{detailLoading ? (
									<DotMatrix label="Loading lead forms" />
								) : forms.length ? (
									<ul>
										{forms.map((f) => (
											<li key={f.id}>
												<Link
													to={`/workspace/${account?.id}/asset/${f.asset_id}/lead-forms`}
												>
													{f.form_name}
												</Link>
											</li>
										))}
									</ul>
								) : (
									<p className="text-sm">No forms use this connection.</p>
								)}
							</CollapsibleCard>
							<CollapsibleCard title="Danger zone" defaultExpanded>
								<p className="mb-4 text-sm">
									This removes the CRM connection with ClickGuard's system. No
									data will be deleted or edited on your CRM.
								</p>
								<AppButton
									label="Delete CRM connection"
									variant="secondary"
									disabled={!canWrite || detailLoading}
									onClick={() => void startDelete()}
								/>
							</CollapsibleCard>
						</>
					)}
				</div>
			</Sheet>
			<Modal
				open={confirmation}
				onClose={() => {
					if (!deleting) setConfirmation(false)
				}}
				size="md"
			>
				<div className="flex flex-col gap-4 p-8">
					<ModalHeader
						title="Delete CRM connection?"
						onClose={() => {
							if (!deleting) setConfirmation(false)
						}}
					/>
					<p>
						This removes this connection from the following forms. Existing CRM
						data is unchanged.
					</p>
					<ul>
						{forms.map((f) => (
							<li key={f.id}>{f.form_name}</li>
						))}
					</ul>
					{deleting ? (
						<DotMatrix label="Deleting CRM connection" />
					) : (
						<AppButton
							label="Delete"
							onClick={async () => {
								if (!selected) return
								setDeleting(true)
								try {
									await api.remove(selected.id)
									setConfirmation(false)
									setSelected(null)
									await reload()
								} catch (e) {
									setError(errorMessage(e))
								} finally {
									setDeleting(false)
								}
							}}
						/>
					)}
				</div>
			</Modal>
		</div>
	)
}
